import { useCallback, useRef } from "react"
import { Transition } from "@headlessui/react"
import DateFilter, {
  DateRangePreset
} from "../../common/ui/activity/date-filter"
import Select from "../../common/ui/form/select"
import { DefaultBtn } from "../../common/ui/buttons"
import { ChevronDown, ChevronUp, Search } from "lucide-react"
import { debounce } from "../../common/utils"

const EVENT_TYPES = [
  { id: "auth_fail", name: "Auth Fail" },
  { id: "auth_success", name: "Auth Success" },
  { id: "auth_logout", name: "Auth Logout" },
  { id: "role_grant", name: "Role Grant" },
  { id: "role_revoke", name: "Role Revoke" },
  { id: "masquerade_start", name: "Masquerade Start" },
  { id: "masquerade_end", name: "Masquerade End" },
  { id: "api_key_create", name: "API Key Create" },
  { id: "api_key_revoke", name: "API Key Revoke" },
  { id: "data_export", name: "Data Export" },
  { id: "config_change", name: "Config Change" },
  { id: "account_lockout", name: "Account Lockout" },
  { id: "data_delete", name: "Data Delete" },
  { id: "billing_error", name: "Billing Error" }
]

const SEVERITIES = [
  { id: "1", name: "Info" },
  { id: "2", name: "Warning" },
  { id: "3", name: "Critical" }
]

interface FilterBarProps {
  dateRange: DateRangePreset
  customStartDate: Date | null
  customEndDate: Date | null
  selectedEventTypes: string[]
  selectedSeverities: string[]
  search: string
  userId: string
  targetId: string
  ipAddress: string
  showAdvanced: boolean
  onDateRangeChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onEventTypesChange: (types: string[]) => void
  onSeveritiesChange: (severities: string[]) => void
  onSearchChange: (search: string) => void
  onUserIdChange: (userId: string) => void
  onTargetIdChange: (targetId: string) => void
  onIpAddressChange: (ipAddress: string) => void
  onToggleAdvanced: () => void
}

// Matches the Select component's Listbox.Button: p-3, border-neutral-200, rounded-md, shadow-sm, sm:text-sm
const INPUT_CLASS =
  "w-full p-3 border border-neutral-200 rounded-md shadow-sm text-sm placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"

const LABEL_CLASS = "block font-medium text-sm"

const FilterBar = ({
  dateRange,
  customStartDate,
  customEndDate,
  selectedEventTypes,
  selectedSeverities,
  search,
  userId,
  targetId,
  ipAddress,
  showAdvanced,
  onDateRangeChange,
  onEventTypesChange,
  onSeveritiesChange,
  onSearchChange,
  onUserIdChange,
  onTargetIdChange,
  onIpAddressChange,
  onToggleAdvanced
}: FilterBarProps) => {
  const searchRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      onSearchChange(value)
    }, 400),
    []
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="[&_>_div_>_div.absolute]:right-auto [&_>_div_>_div.absolute]:left-0">
          <DateFilter
            selectedRange={dateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onRangeChange={onDateRangeChange}
          />
        </div>

        <div className="w-48">
          <Select
            data={EVENT_TYPES}
            selected={selectedEventTypes}
            setSelected={onEventTypesChange}
            label="Event type"
            multiple
          />
        </div>

        <div className="w-40">
          <Select
            data={SEVERITIES}
            selected={selectedSeverities}
            setSelected={onSeveritiesChange}
            label="Severity"
            multiple
          />
        </div>

        <div className="w-48">
          <label className={LABEL_CLASS}>Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search logs..."
              defaultValue={search}
              onChange={e => debouncedSearch(e.target.value)}
              className={`${INPUT_CLASS} pl-9`}
            />
          </div>
        </div>

        <DefaultBtn onClick={onToggleAdvanced}>
          <span className="flex items-center gap-1">
            Advanced
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </DefaultBtn>
      </div>

      <Transition
        show={showAdvanced}
        enter="transition-all duration-200 ease-out"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition-all duration-150 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            <label className={LABEL_CLASS}>User ID</label>
            <div className="mt-1">
              <input
                type="text"
                placeholder="Filter by user ID"
                value={userId}
                onChange={e => onUserIdChange(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
          <div className="w-48">
            <label className={LABEL_CLASS}>Target ID</label>
            <div className="mt-1">
              <input
                type="text"
                placeholder="Filter by target ID"
                value={targetId}
                onChange={e => onTargetIdChange(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
          <div className="w-48">
            <label className={LABEL_CLASS}>IP Address</label>
            <div className="mt-1">
              <input
                type="text"
                placeholder="Filter by IP"
                value={ipAddress}
                onChange={e => onIpAddressChange(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  )
}

export default FilterBar
